<template>
	<section class="signature-editor mb-3" aria-label="서명 변경" :aria-busy="busy ? 'true' : 'false'">
		<div class="signature-editor__header">
			<h6>서명</h6>
			<p>서명 파일은 프로필·계정 정보와 별도로 저장됩니다.</p>
		</div>
		<div class="signature-editor__content">
			<div class="signature-editor__current">
				<div class="signature-editor__label">현재 서명</div>
				<div class="signature-editor__image">
					<img v-if="currentUrl" :src="currentUrl" alt="현재 저장된 서명" draggable="false" @error="onCurrentImageError" />
					<span v-else class="signature-editor__muted">{{ currentMessage }}</span>
				</div>
			</div>
			<div class="signature-editor__upload">
				<div class="signature-editor__label">
					{{ selectedFile ? '변경할 서명' : '새 서명 업로드' }}
					<span v-if="selectedFile" class="signature-editor__badge">저장 전</span>
				</div>
				<div
					class="signature-editor__dropzone"
					:class="{ 'is-dragging': isDragging, 'is-disabled': selectionDisabled, 'has-file': selectedFile }"
					role="button"
					:tabindex="selectionDisabled ? -1 : 0"
					:aria-disabled="selectionDisabled ? 'true' : 'false'"
					:aria-label="selectedFile ? '다른 서명 파일 선택 또는 끌어다 놓기' : '서명 파일 선택 또는 끌어다 놓기'"
					:aria-describedby="`${inputId}-hint`"
					@click="openFilePicker"
					@keydown.enter.prevent="openFilePicker"
					@keydown.space.prevent="openFilePicker"
					@dragenter.prevent.stop="onDragEnter"
					@dragover.prevent.stop="onDragOver"
					@dragleave.prevent.stop="onDragLeave"
					@drop.prevent.stop="onDrop"
				>
					<template v-if="selectedUrl">
						<div class="signature-editor__selected-image">
							<img :src="selectedUrl" alt="변경할 서명 미리보기" draggable="false" @error="onSelectedImageError" />
						</div>
						<span class="signature-editor__filename">{{ selectedFile.name }}</span>
						<span class="signature-editor__muted">{{ selectedFileSize }} · 다른 파일 선택</span>
					</template>
					<template v-else>
						<svg class="signature-editor__upload-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
							<path d="M12 16V3m-5 5 5-5 5 5M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4" />
						</svg>
						<span class="signature-editor__drop-title">{{ isDragging ? '여기에 파일을 놓아 주세요' : '서명 이미지를 끌어다 놓으세요' }}</span>
						<span class="signature-editor__muted">또는 <span class="signature-editor__browse">클릭하여 파일 선택</span></span>
					</template>
					<span v-if="isDragging && selectedFile" class="signature-editor__drop-overlay">놓으면 선택한 파일이 변경됩니다</span>
				</div>
				<div :id="`${inputId}-hint`" class="signature-editor__hint">PNG 또는 JPG · 최대 2 MiB · 이미지 1개</div>
				<input :id="inputId" ref="fileInput" type="file" accept="image/png,image/jpeg,.png,.jpg,.jpeg" class="signature-editor__input" hidden tabindex="-1" aria-label="서명 파일" :disabled="selectionDisabled" @change="selectFile" />
			</div>
		</div>
		<div v-if="error" class="signature-editor__message signature-editor__message--error" role="alert">{{ error }}</div>
		<div v-if="notice" class="signature-editor__message signature-editor__message--success" role="status">{{ notice }}</div>
		<div class="signature-editor__footer">
			<span class="signature-editor__muted" role="status">{{ uploading ? '서명을 저장하는 중…' : loading ? '서명을 확인하는 중…' : selectedFile ? '미리보기를 확인한 뒤 저장해 주세요.' : '' }}</span>
			<div class="signature-editor__actions">
				<b-button v-if="error || needsRefresh" size="sm" variant="outline-secondary" :disabled="busy || disabled" @click="refresh">현재 서명 다시 조회</b-button>
				<b-button v-if="selectedFile" size="sm" variant="outline-secondary" :disabled="selectionDisabled" @click="cancelSelection">선택 취소</b-button>
				<b-button size="sm" variant="primary" :disabled="!selectedFile || selectionDisabled" @click="save">{{ uploading ? '저장 중…' : '서명 저장' }}</b-button>
			</div>
		</div>
	</section>
</template>

<script>
import api from '@/api'

export default {
	name: 'SignatureEditor',
	props: {
		userId: { type: [Number, String], required: true },
		signature: { type: Object, default: null },
		upload: { type: Function, required: true },
		reload: { type: Function, required: true },
		disabled: { type: Boolean, default: false }
	},
	data() {
		return {
			currentSignature: null,
			currentUrl: '',
			selectedFile: null,
			selectedUrl: '',
			loading: false,
			uploading: false,
			needsRefresh: false,
			confirmedSave: false,
			error: '',
			notice: '',
			requestSequence: 0,
			disposed: false,
			dragDepth: 0
		}
	},
	computed: {
		inputId() { return `signature-file-${this._uid}` },
		busy() { return this.loading || this.uploading },
		selectionDisabled() { return this.busy || this.disabled || this.needsRefresh },
		isDragging() { return this.dragDepth > 0 && !this.selectionDisabled },
		selectedFileSize() {
			const size = this.selectedFile?.size || 0
			return size < 1024 * 1024 ? `${Math.max(1, Math.ceil(size / 1024))} KB` : `${(size / 1024 / 1024).toFixed(1)} MB`
		},
		currentMessage() {
			if (this.loading) return '불러오는 중…'
			return this.currentSignature?.exists === false ? '등록된 서명 없음' : '서명을 확인해 주세요.'
		}
	},
	watch: {
		// 부모 모달이 «저장 전» 파일 유무를 알아야 한다 (모달 저장 시 함께 업로드, 닫을 때 확인).
		selectedFile(file) { this.$emit('pending', !!file) },
		userId: {
			immediate: true,
			handler() {
				this.requestSequence++
				this.uploading = false
				this.loading = false
				this.revokeUrl('currentUrl')
				this.cancelSelection()
				this.currentSignature = this.signature
				this.needsRefresh = false
				this.confirmedSave = false
				this.refresh()
			}
		},
		busy(value) { this.$emit('busy', value) },
		selectionDisabled(value) { if (value) this.dragDepth = 0 }
	},
	beforeDestroy() {
		this.disposed = true
		this.requestSequence++
		this.revokeUrl('currentUrl')
		this.revokeUrl('selectedUrl')
		this.$emit('busy', false)
	},
	methods: {
		isCurrent(sequence, userId) {
			return !this.disposed && sequence === this.requestSequence && String(userId) === String(this.userId)
		},
		revokeUrl(key) {
			if (this[key]) URL.revokeObjectURL(this[key])
			this[key] = ''
		},
		cancelSelection() {
			this.dragDepth = 0
			this.revokeUrl('selectedUrl')
			this.selectedFile = null
			if (this.$refs.fileInput) this.$refs.fileInput.value = ''
			this.error = ''
			this.notice = ''
		},
		openFilePicker() {
			if (!this.selectionDisabled) this.$refs.fileInput?.click()
		},
		isFileDrag(event) {
			return Array.from(event.dataTransfer?.types || []).includes('Files') || !!event.dataTransfer?.files?.length
		},
		onDragEnter(event) {
			if (!this.selectionDisabled && this.isFileDrag(event)) this.dragDepth++
		},
		onDragOver(event) {
			if (event.dataTransfer) event.dataTransfer.dropEffect = !this.selectionDisabled && this.isFileDrag(event) ? 'copy' : 'none'
		},
		onDragLeave() { this.dragDepth = Math.max(0, this.dragDepth - 1) },
		onDrop(event) {
			this.dragDepth = 0
			this.selectFiles(Array.from(event.dataTransfer?.files || []))
		},
		selectFile(event) {
			const files = Array.from(event.target.files || [])
			// Reset the native input so choosing the same file again fires change.
			event.target.value = ''
			this.selectFiles(files)
		},
		selectFiles(files) {
			if (this.selectionDisabled) return
			const file = files[0]
			if (!file) return
			this.error = ''
			this.notice = ''
			if (files.length !== 1 || !['image/png', 'image/jpeg'].includes(file.type)) {
				this.error = 'PNG 또는 JPEG 이미지 한 개를 선택해 주세요.'
				return
			}
			if (!file.size || file.size > 2 * 1024 * 1024) {
				this.error = '비어 있지 않은 2 MiB 이하의 이미지를 선택해 주세요.'
				return
			}
			this.revokeUrl('selectedUrl')
			this.selectedFile = file
			this.selectedUrl = URL.createObjectURL(file)
		},
		onSelectedImageError() {
			this.cancelSelection()
			this.error = '이미지 미리보기를 만들 수 없습니다. 다른 PNG 또는 JPEG 파일을 선택해 주세요.'
		},
		onCurrentImageError() {
			this.revokeUrl('currentUrl')
			this.error = '현재 서명 이미지를 표시하지 못했습니다. 다시 조회해 주세요.'
		},
		validateMetadata(meta) {
			if (!meta || typeof meta.exists !== 'boolean' || (meta.exists && !meta.version)) {
				throw new Error('서명 조회 응답을 확인하지 못했습니다.')
			}
			return meta
		},
		async loadImage(meta, sequence, userId) {
			this.validateMetadata(meta)
			let nextUrl = ''
			if (meta.exists) {
				const response = await api.user.getUserSignImg(userId, meta.version)
				if (!this.isCurrent(sequence, userId)) return false
				if (!response.data || !response.data.size) throw new Error('서명 이미지가 비어 있습니다.')
				nextUrl = URL.createObjectURL(response.data)
			}
			if (!this.isCurrent(sequence, userId)) return false
			this.revokeUrl('currentUrl')
			this.currentSignature = meta
			this.currentUrl = nextUrl
			return true
		},
		async refresh() {
			if (this.uploading || this.disabled) return
			const sequence = ++this.requestSequence
			const userId = this.userId
			this.loading = true
			this.error = ''
			try {
				const meta = await this.reload(userId)
				if (!this.isCurrent(sequence, userId)) return
				if (!await this.loadImage(meta, sequence, userId)) return
				const wasUncertain = this.needsRefresh
				if (this.confirmedSave) this.cancelSelection()
				this.needsRefresh = false
				this.notice = this.confirmedSave ? '서명을 저장했습니다.'
					: wasUncertain ? '현재 저장된 서명을 불러왔습니다. 변경할 파일과 확인해 주세요.' : ''
				this.confirmedSave = false
				this.$emit('updated', meta)
			} catch (e) {
				if (this.isCurrent(sequence, userId)) {
					this.error = this.needsRefresh
						? '저장 결과를 확인하지 못했습니다. 현재 서명 다시 조회를 눌러 확인해 주세요.'
						: (e?.response?.data?.msg || e.message || '서명을 불러오지 못했습니다.')
				}
			} finally {
				if (this.isCurrent(sequence, userId)) this.loading = false
			}
		},
		/** 선택만 하고 아직 업로드하지 않은 파일이 있는지 (부모 모달용) */
		hasPendingFile() {
			return !!this.selectedFile
		},
		/**
		 * 부모 모달의 «저장» 이 호출한다. 대기 중인 파일이 있으면 먼저 업로드하고 결과를 돌려준다.
		 * true = 저장됐거나 저장할 파일이 없음, false = 업로드 실패(오류 문구는 편집기에 표시됨) 또는 지금은 저장할 수 없는 상태.
		 */
		async commitPending() {
			if (!this.selectedFile) return true
			if (this.needsRefresh) {
				this.error = '이전 저장 결과를 확인하지 못했습니다. 현재 서명 다시 조회를 누른 뒤 다시 저장해 주세요.'
				return false
			}
			if (this.busy || this.disabled) return false
			return this.save()
		},
		async save() {
			if (!this.selectedFile || this.busy || this.disabled || this.needsRefresh) return false
			const sequence = ++this.requestSequence
			const userId = this.userId
			this.uploading = true
			this.error = ''
			this.notice = ''
			try {
				const result = await this.upload(this.selectedFile, userId)
				if (!this.isCurrent(sequence, userId)) return false
				// A successful write must be confirmed by reading the normalized server PNG.
				this.needsRefresh = true
				if (String(result?.userId) !== String(userId)) throw new Error('서명 저장 응답을 확인하지 못했습니다.')
				this.validateMetadata(result.signature)
				this.confirmedSave = true
				if (!await this.loadImage(result.signature, sequence, userId)) return false
				this.cancelSelection()
				this.needsRefresh = false
				this.confirmedSave = false
				this.notice = '서명을 저장했습니다.'
				this.$emit('updated', result.signature)
				return true
			} catch (e) {
				if (!this.isCurrent(sequence, userId)) return false
				// A lost response or 5xx may have occurred after the atomic replacement.
				if (this.needsRefresh || !e.response || e.response.status >= 500) {
					this.needsRefresh = true
					this.error = '저장 결과를 확인하지 못했습니다. 현재 서명 다시 조회를 눌러 확인해 주세요.'
				} else {
					this.error = e?.response?.data?.msg || '서명을 저장하지 못했습니다. 파일을 확인하고 다시 시도해 주세요.'
				}
				return false
			} finally {
				if (this.isCurrent(sequence, userId)) this.uploading = false
			}
		}
	}
}
</script>

<style scoped>
.signature-editor { padding: 20px; border: 1px solid #e1e7ec; border-radius: 12px; background: #fff; }
.signature-editor__header { margin-bottom: 18px; }
.signature-editor__header h6 { margin: 0 0 6px; font-size: 15px; font-weight: 600; color: #263238; }
.signature-editor__header p { margin: 0; font-size: 12px; line-height: 1.6; color: #697780; }
.signature-editor__content { display: grid; grid-template-columns: minmax(150px, 0.8fr) minmax(0, 1.4fr); gap: 20px; }
.signature-editor__current, .signature-editor__upload { min-width: 0; }
.signature-editor__label { display: flex; align-items: center; gap: 8px; min-height: 22px; margin-bottom: 8px; font-size: 12px; font-weight: 600; color: #47545d; }
.signature-editor__badge { padding: 2px 7px; border-radius: 4px; background: #edf8f6; color: #247c71; font-size: 10px; font-weight: 500; }
.signature-editor__image { height: 164px; display: flex; align-items: center; justify-content: center; padding: 16px; border: 1px solid #e7ecef; border-radius: 8px; background: #fafbfc; }
.signature-editor__image img, .signature-editor__selected-image img { display: block; max-width: 100%; max-height: 100%; object-fit: contain; }
.signature-editor__dropzone { position: relative; min-height: 164px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 7px; padding: 20px 16px; border: 1.5px dashed #c8d5da; border-radius: 8px; background: #f8fbfb; color: #40515a; cursor: pointer; text-align: center; transition: background .15s, border-color .15s; }
.signature-editor__dropzone:not(.is-disabled):hover, .signature-editor__dropzone.is-dragging { background: #eef9f6; border-color: #349c8f; }
.signature-editor__dropzone:focus-visible { outline: 2px solid #349c8f; outline-offset: 3px; }
.signature-editor__dropzone.is-disabled { cursor: not-allowed; opacity: .6; }
.signature-editor__upload-icon { flex-shrink: 0; color: #489b91; margin-bottom: 3px; }
.signature-editor__drop-title { font-size: 13px; font-weight: 500; }
.signature-editor__muted { font-size: 12px; line-height: 1.5; color: #77848c; }
.signature-editor__browse { color: #28867a; font-weight: 500; }
.signature-editor__hint { margin-top: 8px; font-size: 11px; color: #849099; }
.signature-editor__input { display: none !important; }
.signature-editor__selected-image { height: 72px; width: 100%; display: flex; align-items: center; justify-content: center; }
.signature-editor__filename { max-width: 100%; overflow-wrap: anywhere; font-size: 12px; line-height: 1.5; color: #40515a; }
.signature-editor__dropzone.has-file { padding: 14px 16px; }
.signature-editor__drop-overlay { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; padding: 16px; border-radius: 8px; background: #eef9f6f2; font-size: 13px; font-weight: 500; color: #247c71; pointer-events: none; }
.signature-editor__message { margin-top: 14px; padding: 10px 12px; border-radius: 6px; font-size: 12px; line-height: 1.6; overflow-wrap: anywhere; }
.signature-editor__message--error { background: #fff3f2; color: #ba4540; }
.signature-editor__message--success { background: #edf8f3; color: #27815c; }
.signature-editor__footer { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 10px; margin-top: 18px; padding-top: 14px; border-top: 1px solid #edf0f2; }
.signature-editor__actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 8px; margin-left: auto; }
.signature-editor__actions .btn { border-radius: 6px; padding: 7px 12px; font-size: 12px; }
@media (max-width: 560px) {
	.signature-editor { padding: 16px; }
	.signature-editor__content { grid-template-columns: minmax(0, 1fr); gap: 16px; }
	.signature-editor__image { height: 110px; }
}
</style>
